import {
  AlignItems,
  Body2,
  Bold,
  Box,
  Color,
  Display,
  FlexDirection,
  FontFamily,
  Heading2,
  Icon,
  IconVariant,
  Join,
  JustifyContent,
  Space,
  withCitrusTheme,
} from '@gousto-internal/citrus-react'
import React from 'react'
import { CSSObject } from '@emotion/react'

import { OUR_SUPPLIERS_COPY } from '../../config'
import { LandingPageContentSections, TrackingActions, trackCTAClick } from '../../tracking'
import { InlineLink } from '../InlineLink/InlineLink'
import { CWCLinkButton } from '../CWCLinkButton/CWCLinkButton'
import { CWCSupplierCarousel } from './components/CWCSupplierCarousel/CWCSupplierCarousel'

// Full-bleed background band: Porcelain / grey-95 (#F2F3F4) — no Citrus Color token, so raw hex.
// Vertical padding lives here (so the grey extends top/bottom); it centres the capped inner row so
// ultra-wide screens don't left-align the content. Vertical bands: 40 / 40 / 64 / 80 (Figma 80px
// desktop).
const SectionContainer = withCitrusTheme(Box, (theme) => [
  { backgroundColor: '#F2F3F4' } as CSSObject,
  theme.responsiveStyle(['40px', '40px', '64px', '80px'], ['paddingTop']) as CSSObject,
  theme.responsiveStyle(['40px', '40px', '64px', '80px'], ['paddingBottom']) as CSSObject,
])

// Inner row, capped at the Figma x-wide frame width (320:10882 = 1920px) and centred, so beyond
// 1920 the section centres instead of sprawling left with dead space to the right of the carousel.
//
// 4-value responsive bands map to [base(<640), 640+, 768+ (tablet), 1024+ (desktop)] — the CWCHero
// convention (a 3-value array lumps every width ≥768 onto the tablet value). Desktop horizontal
// insets are from the x-wide frame: 128px left, 0 right (the carousel bleeds to the row's right
// edge and crops — a scrollable carousel), 128px gap between the content column and the carousel.
// base/640/768 resolve to the prior paddingH values.
const SectionRow = withCitrusTheme(Box, (theme) => [
  { maxWidth: '1920px', width: '100%' } as CSSObject,
  theme.responsiveStyle(['24px', '24px', '40px', '128px'], ['paddingLeft']) as CSSObject,
  theme.responsiveStyle(['24px', '24px', '40px', '0px'], ['paddingRight']) as CSSObject,
  theme.responsiveStyle(['0px', '0px', '0px', '128px'], ['gap']) as CSSObject,
])

// "Quality you can taste" — Axiforma ExtraBold 36/40, ink #1A2026 (both bespoke, set here).
// Full-length responsiveStyle arrays (one value per breakpoint) so 36/40 wins at ≥768 too — a
// single-value array leaves Heading2's own @media default (28px) to win on desktop.
const SectionHeading = withCitrusTheme(Heading2, (theme) => [
  { color: '#1A2026' },
  theme.responsiveStyle(['36px', '36px', '36px'], ['fontSize']),
  theme.responsiveStyle(['40px', '40px', '40px'], ['lineHeight']),
])

// Bullet copy: 16/24, text/primary (#333D47 = ColdGrey_800).
const BulletText = withCitrusTheme(Body2, (theme) => [
  theme.responsiveStyle(['16px', '16px', '16px'], ['fontSize']),
  theme.responsiveStyle(['24px', '24px', '24px'], ['lineHeight']),
])

function SupplierBullet({ children }: { children: React.ReactNode }) {
  return (
    <Box display={Display.Flex} flexDirection={FlexDirection.Row} alignItems={AlignItems.FlexStart}>
      <Box color={Color.Success_400}>
        <Icon name="tick" variant={IconVariant.Inherit} />
      </Box>
      <Space direction="horizontal" size={2} />
      <BulletText color={Color.ColdGrey_800}>{children}</BulletText>
    </Box>
  )
}

/**
 * CwC "Quality you can taste" suppliers section. A fork of `OurSuppliersSection` — disposable if
 * the CwC experiment loses, and it keeps Control / RAF / paid-search untouched. Two-column from
 * Citrus's ≥768 breakpoint (content left, carousel right); single column stacked below it. The CTA
 * renders in two responsive-visibility spots (in the content column on the wide layout, after the
 * carousel on the stacked layout) — the established `OurSuppliersSection` idiom.
 *
 * Layout uses the Citrus breakpoints via 4-value responsive arrays [base(<640), 640+, 768+ (tablet),
 * 1024+ (desktop)] — the CWCHero convention. Two-column begins at ≥768 (the tablet band shares it
 * rather than the design's tablet single column — a deliberate call, confirmed to keep). The desktop
 * insets (128px left / 128px gap / 0 right) live on `SectionRow`, which is capped at the Figma x-wide
 * frame width (1920px) and centred so wider-than-1920 screens don't left-align with dead space.
 */
export function CWCSuppliersSection() {
  const trackCtaClick = () => {
    trackCTAClick(TrackingActions.OUR_SUPPLIERS, LandingPageContentSections.OUR_SUPPLIERS)
  }

  const cta = (
    <CWCLinkButton
      href={OUR_SUPPLIERS_COPY.ourSuppliersURL}
      variant="secondary"
      onClick={trackCtaClick}
    >
      {OUR_SUPPLIERS_COPY.ctaLabel}
    </CWCLinkButton>
  )

  return (
    <section data-testid="cwcSuppliersSection">
      <SectionContainer display={Display.Flex} justifyContent={JustifyContent.Center}>
        <SectionRow
          display={Display.Flex}
          flexDirection={[FlexDirection.Column, FlexDirection.Column, FlexDirection.Row]}
        >
          {/* Content column — heading, bullets, and (wide layout only) the CTA. Fixed 350px on the
            wide layout (design 408:888) so the heading wraps to two lines and the carousel fills
            the rest. `flexShrink 0` keeps it from being squeezed by the growing carousel. */}
          <Box
            display={Display.Flex}
            flexDirection={FlexDirection.Column}
            alignItems={AlignItems.FlexStart}
            flexShrink={0}
            width={['100%', null, '350px']}
          >
            <SectionHeading fontFamily={FontFamily.ExtraBold}>Quality you can taste</SectionHeading>
            <Space size={6} />
            <Join with={<Space size={4} />}>
              <SupplierBullet>
                <Bold>100% British fresh meat</Bold> with chicken, beef, pork and lamb raised here
                in the UK
              </SupplierBullet>
              <SupplierBullet>
                <Bold>Hand-selected seasonal produce</Bold> from trusted farms in West Sussex,
                Cornwall and beyond
              </SupplierBullet>
              <SupplierBullet>
                Proud to be a{' '}
                <InlineLink backgroundStyle="light" href={OUR_SUPPLIERS_COPY.sustainabilityURL}>
                  B Corp™
                </InlineLink>
                , meeting the highest environmental and social standards
              </SupplierBullet>
            </Join>
            <Space size={8} />
            <Box display={[Display.None, Display.None, Display.Block]} width="100%">
              {cta}
            </Box>
          </Box>

          {/* Carousel column — the supplier carousel and (stacked layout only) the CTA. On the wide
            layout it grows to fill the width beside the fixed content column; `minWidth 0` lets the
            embla carousel's horizontal overflow work inside the flex child. */}
          <Box
            display={Display.Flex}
            flexDirection={FlexDirection.Column}
            alignItems={AlignItems.Center}
            justifyContent={JustifyContent.Center}
            flexGrow={1}
            minWidth="0"
            width={['100%', null, 'auto']}
          >
            <CWCSupplierCarousel />
            <Box
              display={[Display.Block, Display.Block, Display.None]}
              width="100%"
              paddingH={[0, null, 4]}
            >
              <Space size={8} />
              {cta}
            </Box>
          </Box>
        </SectionRow>
      </SectionContainer>
    </section>
  )
}
